import React from 'react';
import {
  Card, Col, Container, Row,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle, faEye, faEyeSlash, faInfoCircle, faSquareMinus, faUndo,
} from '@fortawesome/pro-solid-svg-icons';
import { faEye as faEyeLight } from '@fortawesome/pro-light-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  addChannelIdsToGraphedList,
  addChannelIdsToSelectedList,
  ALERT_FORM_MAX_GRAPHED_CHANNELS,
  removeChannelIdsFromGraphedList,
  removeChannelIdsFromSelectedList,
  selectIsEditingAlertForm,
  selectSensorsData,
  selectThresholdData,
  setRemovedChannelKey,
} from '../../../../redux/slices/alertFormSlice/alertFormSlice';
import MultiAlertGraph from '../../../common/AlertGraph/MultiAlertGraph';
import { selectRoomAlertDeviceList, selectSensorMap } from '../../../../redux/slices/deviceList/deviceListSlice';
import { getChannelDisplayData } from '../../../../utility/channelUtils';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';
import { getAlertGraphThresholdConfig } from '../AlertFormUtility';
import { selectAllThresholdRanges } from '../../../../redux/slices/thresholdRangeSlice/thresholdRangeSlice';

// eslint-disable-next-line react/prop-types
export const AlertCountText = ({ alertCount }) => (
  <small>
    <i className="text-muted">
      This will generate&nbsp;
      {alertCount}
      {alertCount === 1 ? ' alert' : ' alerts'}
    </i>
  </small>
);

function SelectedSensors() {
  const dispatch = useDispatch();
  const {
    selectedChannels,
    graphedChannels,
    graphedChannelColors,
    graphedAlertConfig,
    removedChannelKey,
  } = useSelector(selectSensorsData);
  const sensorMap = useSelector(selectSensorMap);
  const deviceList = useSelector(selectRoomAlertDeviceList);
  const { activeSubscription } = useSelector(selectSessionInformation);
  const hasBulkCreation = activeSubscription && activeSubscription.subscriptionTierLevel > 4;
  const isEdit = useSelector(selectIsEditingAlertForm);

  const {
    thresholdValue,
    thresholdOperator,
    useSharedThreshold,
    thresholdId,
  } = useSelector(selectThresholdData);
  const sharedThresholds = useSelector(selectAllThresholdRanges);
  const alertConfig = {
    ...graphedAlertConfig,
    threshold: getAlertGraphThresholdConfig(
      useSharedThreshold,
      thresholdId,
      sharedThresholds,
      thresholdValue,
      thresholdOperator,
    ),
  };

  const channels = selectedChannels;
  const channelCount = channels.length;

  if (channelCount === 0 && !removedChannelKey) return <></>;

  const graphChannelKeys = graphedChannels;

  const graphColorMap = Object.fromEntries(
    graphedChannels.map((key) => [key, graphedChannelColors[key]]),
  );
  const maxGraphedChannelsReached = graphedChannels.length >= ALERT_FORM_MAX_GRAPHED_CHANNELS
      || graphedChannels.length === selectedChannels.length;

  const onMinusClick = (key) => {
    if (isEdit && selectedChannels.length === 1) {
      dispatch(setRemovedChannelKey({ channelKey: key }));
    }
    dispatch(
      removeChannelIdsFromSelectedList({
        data: { channelIdsToRemove: [key] },
      }),
    );
  };

  const onUndoRemoveSensor = () => {
    dispatch(
      removeChannelIdsFromSelectedList({
        data: { channelIdsToRemove: selectedChannels },
      }),
    );
    dispatch(
      addChannelIdsToSelectedList({
        data: { newChannelIds: [removedChannelKey] },
      }),
    );
    dispatch(setRemovedChannelKey({ channelKey: null }));
  };

  const onMinusAllClick = () => {
    dispatch(
      removeChannelIdsFromSelectedList({
        data: { channelIdsToRemove: selectedChannels },
      }),
    );
  };

  const addAllSensorsToGraph = () => {
    if (isEdit) {
      return;
    }
    const keysToAdd = selectedChannels.filter(
      (key) => !graphedChannels.includes(key),
    );

    if (keysToAdd.length > 0) {
      dispatch(
        addChannelIdsToGraphedList({
          data: { newChannelIds: keysToAdd },
        }),
      );
    }
  };

  const removeAllSensorsFromGraph = () => {
    dispatch(
      removeChannelIdsFromGraphedList({
        data: { channelIdsToRemove: graphedChannels },
      }),
    );
  };

  const toggleGraphChannel = (key) => {
    if (graphedChannels.includes(key)) {
      dispatch(
        removeChannelIdsFromGraphedList({
          data: { channelIdsToRemove: [key] },
        }),
      );
    } else {
      dispatch(
        addChannelIdsToGraphedList({
          data: { newChannelIds: [key] },
        }),
      );
    }
  };

  if (channelCount === 0 && removedChannelKey) {
    const removedSensorDisplayData = getChannelDisplayData(
      removedChannelKey, deviceList, sensorMap,
    );

    return (
      <Card style={{ border: 'none' }}>
        <Card.Body>
          <Row>
            <Col className="col-md-12 col-lg-6">
              <Row>
                <Col>
                  <span className="text-muted" style={{ textDecoration: 'line-through' }}>
                    {`${removedSensorDisplayData.deviceName} > `}
                    <strong>{removedSensorDisplayData.sensorName}</strong>
                    {` (${removedSensorDisplayData.port}) > ${removedSensorDisplayData.channelName}`}
                  </span>

                  <button
                    type="button"
                    className="btn btn-link p-0 ml-2 d-inline-flex align-items-center align-baseline"
                    onClick={onUndoRemoveSensor}
                  >
                    Undo
                    <FontAwesomeIcon className="ml-2" icon={faUndo} />
                  </button>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  Please select a sensor.
                </Col>
              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {channelCount === 1 ? (
        <Card style={{ border: 'none' }}>
          <Card.Body>
            <Row>
              <Col className="col-md-12 col-lg-6">
                {channels.map((channel) => {
                  const {
                    key,
                    deviceName,
                    sensorName,
                    port,
                    channelName,
                  } = getChannelDisplayData(channel, deviceList, sensorMap);
                  return (
                    <Row key={key}>
                      <Col>
                        <FontAwesomeIcon
                          icon={faSquareMinus}
                          onClick={() => onMinusClick(key)}
                          style={{ cursor: 'pointer' }}
                          className="mr-2"
                          title="Remove sensor"
                        />
                        {`${deviceName} > `}
                        <strong>{sensorName}</strong>
                        {port ? ` (${port}) > ${channelName}` : ` > ${channelName}`}
                      </Col>
                    </Row>
                  );
                })}

                {/* Removed sensor (undo) appears under the channel */}
                {removedChannelKey && (
                <Row className="mt-2">
                  <Col>
                    {(() => {
                      const removedSensorDisplayData = getChannelDisplayData(
                        removedChannelKey,
                        deviceList,
                        sensorMap,
                      );
                      return (
                        <>
                          <span className="text-muted" style={{ textDecoration: 'line-through' }}>
                            {`${removedSensorDisplayData.deviceName} > `}
                            <strong>{removedSensorDisplayData.sensorName}</strong>
                            {` (${removedSensorDisplayData.port}) > ${removedSensorDisplayData.channelName}`}
                          </span>

                          <button
                            type="button"
                            className="btn btn-link p-0 ml-2 d-inline-flex align-items-center align-baseline"
                            onClick={onUndoRemoveSensor}
                          >
                            Undo
                            <FontAwesomeIcon className="ml-2" icon={faUndo} />
                          </button>
                        </>
                      );
                    })()}
                  </Col>
                </Row>
                )}

                {!isEdit && (
                <>
                  <Row className="mt-2">
                    <Col>
                      <AlertCountText alertCount={channelCount} />
                    </Col>
                  </Row>
                  {hasBulkCreation && (
                  <Row className="mt-2">
                    <Col>
                      <div className="alert alert-info">
                        <p>
                          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                          You may select more sensors of the same reading type. This
                          will create multiple alerts with identical settings.
                        </p>
                      </div>
                    </Col>
                  </Row>
                  )}
                </>
                )}
              </Col>
              <Col className="col-md-12 offset-lg-1 col-lg-5">
                <MultiAlertGraph
                  channelKeys={graphChannelKeys}
                  alertConfig={alertConfig}
                  colors={graphColorMap}
                  showDateToggle={false}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ) : (
        <Row className="mb-2">
          <Col className="col-md-12 col-lg-6">
            {/* Top bar */}
            <div className="d-flex justify-content-between">
              <div className="pl-2">
                <FontAwesomeIcon
                  icon={faSquareMinus}
                  onClick={onMinusAllClick}
                  style={{ cursor: 'pointer' }}
                  className="mr-2"
                  title="Remove all selected sensors"
                />

                {graphedChannels.length === 0 && (
                <FontAwesomeIcon
                  icon={faEye}
                  onClick={addAllSensorsToGraph}
                  style={{ cursor: 'pointer' }}
                  className="mr-2"
                  title="Add all sensors to graph. You can only have up to 10 sensors in the graph."
                />
                )}

                {graphedChannels.length > 0 && !maxGraphedChannelsReached && (
                <FontAwesomeIcon
                  icon={faEyeLight}
                  onClick={addAllSensorsToGraph}
                  style={{ cursor: 'pointer' }}
                  className="mr-2"
                  title="Add all sensors to graph. You can only have up to 10 sensors in the graph."
                />
                )}

                {maxGraphedChannelsReached && (
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  onClick={removeAllSensorsFromGraph}
                  style={{ cursor: 'pointer' }}
                  className="mr-2"
                  title="Remove all sensors from graph."
                />
                )}
              </div>

              <div className="align-self-start text-right">
                <strong>
                  {channelCount}
                  {' '}
                  sensors selected
                </strong>
              </div>
            </div>

            {/* Scrollable list */}
            <Container
              className="p-2 m-0"
              style={{
                height: '100px',
                border: '1px solid #ccc',
                overflowY: 'scroll',
              }}
            >
              <div>
                {channels.map((channel) => {
                  const {
                    key, deviceName, sensorName, port, channelName,
                  } = getChannelDisplayData(channel, deviceList, sensorMap);
                  const isGraphed = graphedChannels.includes(key);

                  return (
                    <div key={key}>
                      <FontAwesomeIcon
                        icon={faSquareMinus}
                        onClick={() => onMinusClick(key)}
                        style={{ cursor: 'pointer' }}
                        className="mr-2"
                        title="Remove sensor"
                      />

                      {/* Toggle graph icon */}
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isGraphed ? (
                        <FontAwesomeIcon
                          icon={faEye}
                          onClick={() => toggleGraphChannel(key)}
                          style={{ cursor: 'pointer' }}
                          className="mr-2"
                          title="Remove from graph"
                        />
                      ) : graphedChannels.length
                            < ALERT_FORM_MAX_GRAPHED_CHANNELS ? (
                              <FontAwesomeIcon
                                icon={faEyeSlash}
                                onClick={() => toggleGraphChannel(key)}
                                style={{ cursor: 'pointer' }}
                                className="mr-2"
                                title="Add to graph"
                              />
                        ) : (
                          <FontAwesomeIcon
                            icon={faEyeSlash}
                            className="mr-2 text-muted"
                            title="Remove graphed sensors to add more. You can only have up to 10 sensors in the graph."
                          />
                        )}

                      {isGraphed && (
                      <FontAwesomeIcon
                        icon={faCircle}
                        color={graphedChannelColors[key]}
                        className="mr-2"
                      />
                      )}

                      {`${deviceName} > `}
                      <strong>{sensorName}</strong>
                      {` (${port}) > ${channelName}`}
                    </div>
                  );
                })}
              </div>
            </Container>

            <AlertCountText alertCount={channelCount} />
          </Col>

          <Col className="col-md-12 offset-lg-1 col-lg-5">
            <MultiAlertGraph
              channelKeys={graphChannelKeys}
              alertConfig={alertConfig}
              colors={graphColorMap}
              showDateToggle={false}
            />
          </Col>
        </Row>
      )}
      {
          selectedChannels.length > 1 && !hasBulkCreation && (
          <div className="alert mb-0">
            <p className="text-danger mb-0">
              {/* eslint-disable-next-line max-len */}
              On your current Account Level, you can only create one alert at a time. Please remove sensors so that only one is selected.
            </p>
          </div>
          )
      }
    </>
  );
}

export default SelectedSensors;
